import { useMemo } from "react";
import { ChevronLeft, Share, Timer, Heart, Flame } from "lucide-react";
import styles from "./TrainingSummary.module.css";
import MetricCard from "./MetricCard";
import { HeartRateChartBox } from "./HeartRateChart";
import ZoneLegend from "./ZoneLegend";
import { useBrand } from "../../context/BrandContext";
import { useClubLicense } from "../../context/ClubLicenseContext";
import { useTranslation } from "react-i18next";
import {
  calculateMHR,
  calculateZoneDurations,
  calculateCalories,
  formatDuration,
} from "./zoneUtils";

function normalizeDateTime(dt) {
  if (dt instanceof Date && !Number.isNaN(dt.getTime())) return dt;
  const d = new Date(dt);
  return Number.isNaN(d.getTime()) ? new Date() : d;
}

/**
 * @param {{
 *   exercise: {
 *     name: string;
 *     typeCategory?: string;
 *     subtype?: string;
 *     dateTime: Date;
 *     durationSec: number;
 *     durationLabel?: string;
 *   },
 *   hrPoints: { bpm: number; timestamp?: string }[],
 *   user?: { age?: number; gender?: "male"|"female"; weightKg?: number },
 *   serverStats?: { maxHr?: number; avgHr?: number; minHr?: number },
 *   onBack?: () => void,
 *   onShare?: () => void,
 *   sessionContextSlot?: React.ReactNode,
 *   topSlot?: React.ReactNode,
 * }} props
 */
export default function TrainingSummary({
  exercise,
  hrPoints = [],
  user = {},
  serverStats = {},
  onBack,
  onShare,
  sessionContextSlot = null,
  topSlot = null,
  videoUrl = null,
}) {
  const { brand } = useBrand();
  const { t } = useTranslation();
  const { hasFeature } = useClubLicense();
  const metricColors = useMemo(
    () => ({
      primary: brand?.color_primary ?? "#0877BC",
      secondary: brand?.color_secondary ?? "#46528b",
      calories: "#FF9900",
      maxHr: "#F54236",
      chartHeart: "#F54236",
    }),
    [brand?.color_primary, brand?.color_secondary]
  );

  const mhr = useMemo(
    () => calculateMHR({ age: user.age, gender: user.gender }),
    [user.age, user.gender]
  );

  const { maxHR, minHR, avgHR } = useMemo(() => {
    if (!hrPoints.length) {
      if (serverStats.maxHr != null) {
        return {
          maxHR: serverStats.maxHr,
          minHR: serverStats.minHr ?? 0,
          avgHR: serverStats.avgHr ?? 0,
        };
      }
      return { maxHR: 0, minHR: 0, avgHR: 0 };
    }
    const bpms = hrPoints.map((p) => p.bpm);
    const sum = bpms.reduce((a, b) => a + b, 0);
    const cMax = Math.max(...bpms);
    const cMin = Math.min(...bpms);
    const cAvg = Math.round(sum / bpms.length);
    if (serverStats.maxHr != null) {
      return {
        maxHR: serverStats.maxHr,
        minHR:
          serverStats.minHr != null && serverStats.minHr > 0
            ? serverStats.minHr
            : cMin,
        avgHR:
          serverStats.avgHr != null && serverStats.avgHr > 0
            ? serverStats.avgHr
            : cAvg,
      };
    }
    return { maxHR: cMax, minHR: cMin, avgHR: cAvg };
  }, [hrPoints, serverStats]);

  const safeTotalSec = useMemo(() => {
    const t = Number(exercise.durationSec);
    if (Number.isFinite(t) && t > 0) return t;
    return hrPoints.length > 0 ? Math.max(1, hrPoints.length) : 0;
  }, [exercise.durationSec, hrPoints.length]);

  const zoneDurations = useMemo(
    () =>
      calculateZoneDurations({
        mhr,
        hrPoints,
        totalDurationSec: safeTotalSec,
      }),
    [mhr, hrPoints, safeTotalSec]
  );

  const calories = useMemo(
    () => calculateCalories({ zoneDurations, weightKg: user.weightKg ?? 70 }),
    [zoneDurations, user.weightKg]
  );

  const timeLabels = useMemo(() => {
    const n = hrPoints.length;
    if (!n) return [];
    const total = safeTotalSec > 0 ? safeTotalSec : n;
    return Array.from({ length: n }, (_, i) =>
      formatDuration(n === 1 ? 0 : (total * i) / (n - 1))
    );
  }, [hrPoints, safeTotalSec]);

  const durationText =
    exercise.durationLabel ?? formatDuration(safeTotalSec);

  const dateText = normalizeDateTime(exercise.dateTime).toLocaleString(
    undefined,
    {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }
  );

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {sessionContextSlot}
        {topSlot}
        <div className={styles.header}>
          <button
            type="button"
            className={styles.iconBtn}
            onClick={onBack}
            aria-label={t("back")}
          >
            <ChevronLeft size={24} />
          </button>
          <h1 className={styles.title}>{t("training_summary_title")}</h1>
          <button
            type="button"
            className={styles.iconBtn}
            onClick={onShare}
            aria-label={t("share")}
          >
            <Share size={22} />
          </button>
        </div>

        <div className={`${styles.card} ${styles.summaryCard}`}>
          <h2 className={styles.summaryTitle}>{exercise.name}</h2>
          {exercise.typeCategory && (
            <p className={styles.summaryType}>{exercise.typeCategory}</p>
          )}
          {exercise.executionModeLabel && (
            <p className={styles.summaryType}>{exercise.executionModeLabel}</p>
          )}
          {exercise.subtype && (
            <p className={styles.summarySubtype}>{exercise.subtype}</p>
          )}
          <p className={styles.summaryDate}>{dateText}</p>
        </div>

        {videoUrl && (
          <div className={`${styles.card} ${styles.videoCard}`}>
            <div className={styles.videoTitleRow}>
              <span className={styles.videoTitle}>📹 {t("evaluation_recording")}</span>
            </div>
            <div className={styles.videoPlayerContainer}>
              <video className={styles.videoPlayer} src={videoUrl} controls playsInline />
            </div>
          </div>
        )}

        <div className={styles.metricsGrid}>
          <MetricCard
            Icon={Timer}
            color={metricColors.primary}
            value={durationText}
            label={t("duration")}
          />
          <MetricCard
            Icon={Heart}
            color={metricColors.primary}
            value={minHR}
            label={t("training_hr_min")}
            smallLabel="MIN"
          />
          <MetricCard
            Icon={Flame}
            color={metricColors.calories}
            value={`${calories} kcal`}
            label={t("calories")}
          />
          <MetricCard
            Icon={Heart}
            color={metricColors.secondary}
            value={avgHR}
            label={t("training_hr_avg")}
            smallLabel="AVG"
          />
          <MetricCard
            Icon={Heart}
            color={metricColors.maxHr}
            value={maxHR}
            label={t("training_hr_max")}
            smallLabel="MAX"
            fullWidth
          />
        </div>

        <div className={`${styles.card} ${styles.chartCard}`}>
          <div className={styles.chartTitleRow}>
            <Heart
              size={20}
              color={metricColors.chartHeart}
              fill={metricColors.chartHeart}
            />
            <h3 className={styles.chartTitle}>{t("heart_rate_chart_title")}</h3>
          </div>
          <div className={styles.chartBox}>
            {!hasFeature("hr_biofeedback") ? (
              <div style={{ textAlign: "center", padding: "24px 16px" }}>
                <p style={{ margin: "0 0 6px", fontWeight: 600, color: "var(--brand-text, #1e293b)" }}>
                  {t("hrAddonRequiredTitle", "Heart Rate Sensor Add-on")}
                </p>
                <p style={{ margin: 0, fontSize: "0.85rem", color: "#64748b" }}>
                  {t("hrAddonRequiredDesc", "Biofeedback HR zones and effort analysis require the Heart Rate Sensor Add-on using the mobile app (iOS / Android).")}
                </p>
              </div>
            ) : hrPoints.length > 0 ? (
              <HeartRateChartBox
                key={`hr-chart-${mhr}`}
                hrPoints={hrPoints}
                mhr={mhr}
                timeLabels={timeLabels}
              />
            ) : (
              <p className={styles.chartEmpty}>{t("no_heart_rate_data")}</p>
            )}
          </div>
        </div>

        <ZoneLegend zoneDurations={zoneDurations} />
      </div>
    </div>
  );
}
